import { ChangeEvent, useEffect, useRef, useState } from "react";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import ButtonBase from "@mui/material/ButtonBase";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import AddIcon from "@mui/icons-material/Add";
import { alpha } from "@mui/material/styles";
import { COMPACT_QUERY } from "../../../utils/hooks/useCompact";

const compact = "@media " + COMPACT_QUERY;

// Matches the image types the products API accepts.
const ACCEPTED_IMAGE_TYPES = "image/jpeg,image/png,image/webp";

const DROP_AREA_HEIGHT = 180;
const COMPACT_DROP_AREA_HEIGHT = 132;
/** Tint strength of the primary colour filling the empty drop area. */
const DROP_AREA_TINT_OPACITY = 0.06;

interface ImageUploadFieldProps {
  /** Bold label including the slot's expected size, e.g. "รูปภาพสินค้า (150x150px)". */
  label: string;
  /** Image already stored on the product; shown until a new file is picked. */
  imageUrl?: string | null;
  /** File picked but not uploaded yet — it takes over the preview. */
  file?: File | null;
  /** File types the picker offers; the products API's image types by default. */
  accept?: string;
  onSelect: (file: File) => void;
  /** Omitted where a stored image cannot be removed; the "ลบรูปภาพ" button is then hidden. */
  onClear?: () => void;
}

/**
 * Presentational image slot: it previews and reports the choice, and never talks
 * to the API — the owning form decides when the file is uploaded.
 */
export default function ImageUploadField({
  label,
  imageUrl,
  file,
  accept = ACCEPTED_IMAGE_TYPES,
  onSelect,
  onClear,
}: ImageUploadFieldProps) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [objectUrl, setObjectUrl] = useState<string | null>(null);

  useEffect(() => {
    if (!file) {
      setObjectUrl(null);
      return;
    }
    const url = URL.createObjectURL(file);
    setObjectUrl(url);
    // Release the blob as soon as another file replaces it or the field unmounts.
    return () => URL.revokeObjectURL(url);
  }, [file]);

  const previewUrl = objectUrl ?? imageUrl ?? null;

  const handleOpenPicker = () => {
    inputRef.current?.click();
  };

  const handleFileChange = (event: ChangeEvent<HTMLInputElement>) => {
    const selected = event.target.files?.[0];
    // Reset the input so picking the same file again still fires onChange.
    event.target.value = "";
    if (selected) onSelect(selected);
  };

  return (
    <Box sx={{ flex: 1, minWidth: 0 }}>
      <Typography variant="body2" fontWeight={700} sx={{ mb: 0.75 }}>
        {label}
      </Typography>

      <ButtonBase
        onClick={handleOpenPicker}
        sx={{
          width: "100%",
          height: DROP_AREA_HEIGHT,
          [compact]: { height: COMPACT_DROP_AREA_HEIGHT },
          borderRadius: 2,
          overflow: "hidden",
          border: (theme) => `1px dashed ${theme.palette.primary.main}`,
          bgcolor: (theme) =>
            alpha(theme.palette.primary.main, DROP_AREA_TINT_OPACITY),
        }}
      >
        {previewUrl ? (
          <Box
            component="img"
            src={previewUrl}
            alt={label}
            sx={{ maxWidth: "100%", maxHeight: "100%", objectFit: "contain" }}
          />
        ) : (
          <Stack
            direction="row"
            alignItems="center"
            gap={0.5}
            sx={{ color: (theme) => theme.palette.primary.main }}
          >
            <AddIcon fontSize="small" />
            <Typography variant="body2" fontWeight={500}>
              เพิ่มรูปภาพ
            </Typography>
          </Stack>
        )}
      </ButtonBase>

      <input
        ref={inputRef}
        type="file"
        accept={accept}
        onChange={handleFileChange}
        style={{ display: "none" }}
      />

      {previewUrl ? (
        <Stack direction="row" gap={1} sx={{ mt: 1 }}>
          <Button size="small" onClick={handleOpenPicker}>
            เปลี่ยนรูปภาพ
          </Button>
          {onClear ? (
            <Button size="small" color="error" onClick={onClear}>
              ลบรูปภาพ
            </Button>
          ) : null}
        </Stack>
      ) : null}
    </Box>
  );
}
